import React, { useState, ReactElement } from 'react';

import { Stack } from '@mui/system';
import { KeyboardArrowRight } from '@mui/icons-material';
import { Menu, Badge, Button, Tooltip, MenuItem, MenuList, IconButton, ListItemText, CircularProgress } from '@mui/material';

interface INestedMenuItem {
  label: string;
  icon?: ReactElement;
  onClick?: Function;
  disabled?: boolean;
  tooltip?: {
    title: string;
    placement?: string;
  }
  children?: INestedMenuItem[];
  badge?: {
    content: string | number;
    color: string;
  }
  color?: string;
}

interface NestedMenuItemProps extends INestedMenuItem {
  children?: INestedMenuItem[];
  onClose: () => void;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({ label, children, icon, onClick, color, onClose, badge, disabled = false, tooltip }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isSubMenu = Array.isArray(children);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (isSubMenu) {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    } else {
      if (onClick) onClick();
      onClose();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={tooltip?.title} placement={tooltip?.placement ?? 'top' as any} arrow>
        <span>
          <MenuItem onClick={handleClick} sx={{ color }} disabled={disabled}>
            <Stack direction="row" justifyContent="space-between" width="100%" spacing={1}>
              {badge && (
                <Badge
                  badgeContent={badge.content}
                  color={badge.color as 'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning'}
                  sx={{ marginRight: 1 }}
                >
                  <Stack direction="row" spacing={2}>
                    {icon}
                    <ListItemText >{label}</ListItemText>
                  </Stack>
                </Badge>
              )}

              {!badge && (

                <Stack direction="row" spacing={1}>
                  {icon}
                  <ListItemText >{label}</ListItemText>
                </Stack>

              )}

              {isSubMenu && <KeyboardArrowRight sx={{ mr: '0 !important' }} color='disabled' />}
            </Stack>
          </MenuItem>
        </span>
      </Tooltip>
      {isSubMenu && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {children.map((child, index) => (
            <NestedMenuItem key={index} {...child} onClose={onClose} />
          ))}
        </Menu>
      )}
    </>
  );
};


type TriggerElementProps = {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

type ButtonTriggerProps = TriggerElementProps & {
  buttonText: string;
}

const ButtonTrigger: React.FC<ButtonTriggerProps> = ({ onClick, buttonText }) => (
  <Button onClick={onClick}>{buttonText}</Button>
);

type IconTriggerProps = TriggerElementProps & {
  icon: ReactElement;
}

const IconTrigger: React.FC<IconTriggerProps> = ({ onClick, icon }) => (
  <IconButton onClick={onClick}>{icon}</IconButton>
);

type TriggerType =
  | { type: 'button'; buttonText: string }
  | { type: 'icon'; icon: ReactElement };

interface INestedMenuWithTriggerProps {
  menuItems: INestedMenuItem[];
  trigger: TriggerType;
}

interface INestedMenuProps {
  anchorEl: null | HTMLElement;
  menuItems: INestedMenuItem[];
  isLoading?: boolean;
  onClose: () => void;
}


const NestedMenuWithTrigger: React.FC<INestedMenuWithTriggerProps> = ({ menuItems, trigger }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderTrigger = () => {
    switch (trigger.type) {
      case 'button':
        return <ButtonTrigger onClick={handleClick} buttonText={trigger.buttonText} />;
      case 'icon':
        return <IconTrigger onClick={handleClick} icon={trigger.icon} />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderTrigger()}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {menuItems.map((item, index) => (
          <NestedMenuItem key={index} {...item} onClose={handleClose} />
        ))}
      </Menu>
    </>
  );
};

export const NestedMenu: React.FC<INestedMenuProps> = ({ menuItems, anchorEl, onClose, isLoading }) => (
  <MenuList
    variant='selectedMenu'
  >
    {
      isLoading ? (
        <Stack direction="column" spacing={0} sx={{ height: 200 }} justifyContent='center' alignItems='center'>
          <CircularProgress size={20} />
        </Stack>
      )
        :
        <>
          {menuItems.map((item, index) => (
            <NestedMenuItem key={index} {...item} onClose={onClose} />
          ))}
        </>
    }

  </MenuList >
);

export default NestedMenuWithTrigger;